import { Component, Input, Output, EventEmitter, OnInit, OnChanges, SimpleChanges, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { DialogModule } from 'primeng/dialog';
import { ButtonModule } from 'primeng/button';
import { InputTextModule } from 'primeng/inputtext';
import { InputNumberModule } from 'primeng/inputnumber';
import { ProgressSpinnerModule } from 'primeng/progressspinner';
import { MessageService } from 'primeng/api';
import { ToastModule } from 'primeng/toast';
import { RouterModule } from '@angular/router';
import {
  BillsPaymentService,
  AirtimeService,
  DataService,
  CableService,
  ElectricityService,
  BillingPeriod,
  DataPlan,
  DiscoPlan,
  CablePlan,
  ProcessAirtimePaymentRequest,
  ProcessDataPaymentRequest,
  ProcessElectricityPaymentRequest,
  ProcessCablePaymentRequest
} from '../../../services/bills-payment.service';
import { SettingsService } from '../../../services/settings.service';
import { ShoppingCardService } from '../../../services/shopping-card.service';
import { CustomerCardService, ArkifiHubCustomerCardDto } from '../../../services/customer-card.service';
import { ArkifiHubPlatformService } from '../../../services/arkifihub-platform.service';
import { BillType } from '../bills-payment.component';

@Component({
  selector: 'app-bills-purchase-modal',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    RouterModule,
    DialogModule,
    ButtonModule,
    InputTextModule,
    InputNumberModule,
    ProgressSpinnerModule,
    ToastModule
  ],
  providers: [MessageService],
  templateUrl: './bills-purchase-modal.component.html',
  styleUrl: './bills-purchase-modal.component.css'
})
export class BillsPurchaseModalComponent implements OnInit, OnChanges {
  @Input() billType: BillType | null = null;
  @Input() airtimeServices: AirtimeService[] = [];
  @Input() dataServices: DataService[] = [];
  @Input() cableServices: CableService[] = [];
  @Input() electricityServices: ElectricityService[] = [];
  @Input() platformId: string = '';
  @Input() visible: boolean = false;
  
  @Output() visibleChange = new EventEmitter<boolean>();
  @Output() success = new EventEmitter<void>();

  // Form states
  isProcessing = signal(false);
  isLoadingPlans = signal(false);
  isValidating = signal(false);
  
  // Common fields
  selectedServiceId = signal<string>('');
  amount = signal<number | null>(null);
  phoneNumber = signal<string>('');
  transactionPin = signal<string>('');
  
  // Data-specific fields
  billingPeriods = signal<BillingPeriod[]>([]);
  selectedPeriod = signal<string>('');
  dataPlans = signal<DataPlan[]>([]);
  selectedDataPlan = signal<DataPlan | null>(null);
  
  // Electricity-specific fields
  meterNumber = signal<string>('');
  discoPlans = signal<DiscoPlan[]>([]);
  selectedDiscoPlan = signal<DiscoPlan | null>(null);
  validatedCustomer = signal<any>(null);
  
  // Cable-specific fields
  smartCardNumber = signal<string>('');
  cablePlans = signal<CablePlan[]>([]);
  selectedCablePlan = signal<CablePlan | null>(null);
  validatedCableCustomer = signal<any>(null);

  // Payment method selection
  paymentMethod = signal<'shoppingCard' | 'savedCard'>('shoppingCard');
  supportsSavedCards = signal<boolean>(false);
  savedCards = signal<ArkifiHubCustomerCardDto[]>([]);
  selectedCardId = signal<string | null>(null);
  isLoadingCards = signal(false);

  constructor(
    private billsPaymentService: BillsPaymentService,
    private settingsService: SettingsService,
    private shoppingCardService: ShoppingCardService,
    private customerCardService: CustomerCardService,
    private arkifiHubPlatformService: ArkifiHubPlatformService,
    private messageService: MessageService
  ) {}

  ngOnInit(): void {
    // Initialization
  }

  ngOnChanges(changes: SimpleChanges): void {
    // Load billing periods when billType changes to 'data' or when modal becomes visible
    if (changes['billType'] || changes['visible']) {
      if (this.billType?.id === 'data' && this.visible) {
        this.loadBillingPeriods();
      }
      
      // Check supports saved cards and load saved cards when modal becomes visible
      if (changes['visible'] && this.visible && this.platformId) {
        this.loadSupportsSavedCardsAndCards();
      }
    }
  }

  loadSupportsSavedCardsAndCards(): void {
    if (!this.platformId) return;
    const environment = this.settingsService.getEnvironment();
    this.arkifiHubPlatformService.getSupportsSavedCards(this.platformId, environment).subscribe({
      next: (res) => {
        const supported = res.data?.supportsSavedCards ?? false;
        this.supportsSavedCards.set(supported);
        if (supported) {
          this.loadSavedCards();
        } else {
          this.savedCards.set([]);
          this.selectedCardId.set(null);
          if (this.paymentMethod() === 'savedCard') {
            this.paymentMethod.set('shoppingCard');
          }
        }
      },
      error: () => {
        this.supportsSavedCards.set(false);
        this.savedCards.set([]);
      }
    });
  }

  async loadSavedCards(): Promise<void> {
    if (!this.platformId) return;
    
    this.isLoadingCards.set(true);
    try {
      const environment = this.settingsService.getEnvironment();
      const result = await this.customerCardService.getCustomerCards(this.platformId, environment);
      
      if (result.success && result.data) {
        // Filter only active cards
        const activeCards = result.data.filter(card => card.isActive);
        this.savedCards.set(activeCards);
      }
    } catch (error) {
      console.error('Error loading saved cards:', error);
    } finally {
      this.isLoadingCards.set(false);
    }
  }

  loadBillingPeriods(): void {
    this.billsPaymentService.getBillingPeriods(this.platformId).subscribe({
      next: (response) => {
        if (response.success) {
          this.billingPeriods.set(response.data);
        }
      },
      error: (error) => {
        console.error('Error loading billing periods:', error);
      }
    });
  }

  onDataServiceChange(): void {
    this.selectedPeriod.set('');
    this.dataPlans.set([]);
    this.selectedDataPlan.set(null);
  }

  onPeriodChange(): void {
    const serviceId = this.selectedServiceId();
    const periodId = this.selectedPeriod();
    
    if (!serviceId || !periodId) return;
    
    const selectedService = this.dataServices.find(s => s.id === serviceId);
    const selectedPeriod = this.billingPeriods().find(p => p.id.toString() === periodId);
    if (!selectedService || !selectedPeriod) return;
    
    this.loadDataPlans(selectedService.name, selectedPeriod.name);
  }

  loadDataPlans(serviceName: string, period: string): void {
    this.isLoadingPlans.set(true);
    this.billsPaymentService.getDataPlans(serviceName, period, this.platformId).subscribe({
      next: (response) => {
        if (response.success) {
          this.dataPlans.set(response.data);
        } else {
          this.messageService.add({
            severity: 'error',
            summary: 'Error',
            detail: response.message || 'Failed to load data plans',
            life: 3000
          });
        }
        this.isLoadingPlans.set(false);
      },
      error: (error) => {
        console.error('Error loading data plans:', error);
        this.messageService.add({
          severity: 'error',
          summary: 'Error',
          detail: 'Failed to load data plans',
          life: 3000
        });
        this.isLoadingPlans.set(false);
      }
    });
  }

  onDataPlanSelect(plan: DataPlan): void {
    this.selectedDataPlan.set(plan);
    this.amount.set(plan.amount || 0);
  }

  onElectricityServiceChange(): void {
    this.discoPlans.set([]);
    this.selectedDiscoPlan.set(null);
    this.validatedCustomer.set(null);
    this.meterNumber.set('');
    
    const serviceId = this.selectedServiceId();
    if (!serviceId) return;
    
    const selectedService = this.electricityServices.find(s => s.id === serviceId);
    if (!selectedService) return;
    
    this.loadDiscoPlans(selectedService.name);
  }

  loadDiscoPlans(discoName: string): void {
    this.isLoadingPlans.set(true);
    this.billsPaymentService.getDiscoPlans(discoName, this.platformId).subscribe({
      next: (response) => {
        if (response.success) {
          this.discoPlans.set(response.data);
        } else {
          this.messageService.add({
            severity: 'error',
            summary: 'Error',
            detail: response.message || 'Failed to load electricity plans',
            life: 3000
          });
        }
        this.isLoadingPlans.set(false);
      },
      error: (error) => {
        console.error('Error loading disco plans:', error);
        this.messageService.add({
          severity: 'error',
          summary: 'Error',
          detail: 'Failed to load electricity plans',
          life: 3000
        });
        this.isLoadingPlans.set(false);
      }
    });
  }

  onDiscoPlanSelect(plan: DiscoPlan): void {
    this.selectedDiscoPlan.set(plan);
  }

  validateMeterNumber(): void {
    const meterNum = this.meterNumber();
    const discoPlan = this.selectedDiscoPlan();
    
    if (!meterNum || !discoPlan) {
      this.messageService.add({
        severity: 'warn',
        summary: 'Validation Required',
        detail: 'Please enter meter number and select a plan',
        life: 3000
      });
      return;
    }
    
    this.isValidating.set(true);
    this.billsPaymentService.validateElectricityCustomer({
      meterNumber: meterNum,
      billerSlug: discoPlan.billerSlug,
      billerId: discoPlan.billerId,
      packageSlug: discoPlan.slug
    }, this.platformId).subscribe({
      next: (response) => {
        if (response.success && response.data.isValid) {
          this.validatedCustomer.set(response.data);
          this.messageService.add({
            severity: 'success',
            summary: 'Validation Successful',
            detail: `Validated: ${response.data.customerName}`,
            life: 3000
          });
        } else {
          this.messageService.add({
            severity: 'error',
            summary: 'Validation Failed',
            detail: response.data?.message || 'Invalid meter number',
            life: 3000
          });
        }
        this.isValidating.set(false);
      },
      error: (error) => {
        console.error('Error validating meter:', error);
        this.messageService.add({
          severity: 'error',
          summary: 'Error',
          detail: 'Failed to validate meter number',
          life: 3000
        });
        this.isValidating.set(false);
      }
    });
  }

  onCableServiceChange(): void {
    this.cablePlans.set([]);
    this.selectedCablePlan.set(null);
    this.validatedCableCustomer.set(null);
    this.smartCardNumber.set('');
    
    const serviceId = this.selectedServiceId();
    if (!serviceId) return;
    
    const selectedService = this.cableServices.find(s => s.id === serviceId);
    if (!selectedService) return;
    
    this.loadCablePlans(selectedService.name);
  }

  loadCablePlans(cableTvName: string): void {
    this.isLoadingPlans.set(true);
    this.billsPaymentService.getCablePlans(cableTvName, this.platformId).subscribe({
      next: (response) => {
        if (response.success) {
          this.cablePlans.set(response.data);
        } else {
          this.messageService.add({
            severity: 'error',
            summary: 'Error',
            detail: response.message || 'Failed to load cable plans',
            life: 3000
          });
        }
        this.isLoadingPlans.set(false);
      },
      error: (error) => {
        console.error('Error loading cable plans:', error);
        this.messageService.add({
          severity: 'error',
          summary: 'Error',
          detail: 'Failed to load cable plans',
          life: 3000
        });
        this.isLoadingPlans.set(false);
      }
    });
  }

  onCablePlanSelect(plan: CablePlan): void {
    this.selectedCablePlan.set(plan);
    this.amount.set(plan.amount || 0);
  }

  validateSmartCard(): void {
    const smartCard = this.smartCardNumber();
    const cablePlan = this.selectedCablePlan();
    
    if (!smartCard || !cablePlan) {
      this.messageService.add({
        severity: 'warn',
        summary: 'Validation Required',
        detail: 'Please enter smart card number and select a plan',
        life: 3000
      });
      return;
    }
    
    this.isValidating.set(true);
    this.billsPaymentService.validateCableCustomer({
      smartCardNumber: smartCard,
      billerSlug: cablePlan.billerSlug,
      billerId: cablePlan.billerId
    }, this.platformId).subscribe({
      next: (response) => {
        if (response.success && response.data.isValid) {
          this.validatedCableCustomer.set(response.data);
          this.messageService.add({
            severity: 'success',
            summary: 'Validation Successful',
            detail: `Validated: ${response.data.customerName}`,
            life: 3000
          });
        } else {
          this.messageService.add({
            severity: 'error',
            summary: 'Validation Failed',
            detail: response.data?.message || 'Invalid smart card number',
            life: 3000
          });
        }
        this.isValidating.set(false);
      },
      error: (error) => {
        console.error('Error validating smart card:', error);
        this.messageService.add({
          severity: 'error',
          summary: 'Error',
          detail: 'Failed to validate smart card number',
          life: 3000
        });
        this.isValidating.set(false);
      }
    });
  }

  processPurchase(): void {
    if (!this.validateForm()) {
      return;
    }

    if (!this.billType) return;

    switch (this.billType.id) {
      case 'airtime':
        this.purchaseAirtime();
        break;
      case 'data':
        this.purchaseData();
        break;
      case 'cable':
        this.purchaseCable();
        break;
      case 'electricity':
        this.purchaseElectricity();
        break;
    }
  }

  validateForm(): boolean {
    if (!this.billType) return false;

    // Airtime validation
    if (this.billType.id === 'airtime') {
      if (!this.selectedServiceId()) {
        this.messageService.add({
          severity: 'warn',
          summary: 'Validation Error',
          detail: 'Please select a network',
          life: 3000
        });
        return false;
      }
      if (!this.amount() || this.amount()! < 50 || this.amount()! > 50000) {
        this.messageService.add({
          severity: 'warn',
          summary: 'Validation Error',
          detail: 'Amount must be between ₦50 and ₦50,000',
          life: 3000
        });
        return false;
      }
      if (!this.phoneNumber() || this.phoneNumber().length < 11) {
        this.messageService.add({
          severity: 'warn',
          summary: 'Validation Error',
          detail: 'Please enter a valid phone number',
          life: 3000
        });
        return false;
      }
      // PIN is always required, regardless of payment method
      if (!this.transactionPin() || this.transactionPin().length < 4) {
        this.messageService.add({
          severity: 'warn',
          summary: 'Validation Error',
          detail: 'Please enter your transaction PIN',
          life: 3000
        });
        return false;
      }
      // If using saved card, validate card selection
      if (this.paymentMethod() === 'savedCard') {
        if (!this.selectedCardId()) {
          this.messageService.add({
            severity: 'warn',
            summary: 'Validation Error',
            detail: 'Please select a saved card',
            life: 3000
          });
          return false;
        }
      }
    }

    // Data validation
    if (this.billType.id === 'data') {
      if (!this.selectedDataPlan()) {
        this.messageService.add({
          severity: 'warn',
          summary: 'Validation Error',
          detail: 'Please select a data plan',
          life: 3000
        });
        return false;
      }
      if (!this.phoneNumber() || this.phoneNumber().length < 11) {
        this.messageService.add({
          severity: 'warn',
          summary: 'Validation Error',
          detail: 'Please enter a valid phone number',
          life: 3000
        });
        return false;
      }
      // PIN is always required, regardless of payment method
      if (!this.transactionPin() || this.transactionPin().length < 4) {
        this.messageService.add({
          severity: 'warn',
          summary: 'Validation Error',
          detail: 'Please enter your transaction PIN',
          life: 3000
        });
        return false;
      }
      // If using saved card, validate card selection
      if (this.paymentMethod() === 'savedCard') {
        if (!this.selectedCardId()) {
          this.messageService.add({
            severity: 'warn',
            summary: 'Validation Error',
            detail: 'Please select a saved card',
            life: 3000
          });
          return false;
        }
      }
    }

    // Electricity validation
    if (this.billType.id === 'electricity') {
      if (!this.validatedCustomer()) {
        this.messageService.add({
          severity: 'warn',
          summary: 'Validation Error',
          detail: 'Please validate meter number first',
          life: 3000
        });
        return false;
      }
      const minElectric = this.validatedCustomer()?.minAmount ?? 100;
      if (!this.amount() || this.amount()! < minElectric) {
        this.messageService.add({
          severity: 'warn',
          summary: 'Validation Error',
          detail: `Amount must be at least ₦${minElectric.toFixed(2)}`,
          life: 3000
        });
        return false;
      }
      // PIN is always required, regardless of payment method
      if (!this.transactionPin() || this.transactionPin().length < 4) {
        this.messageService.add({
          severity: 'warn',
          summary: 'Validation Error',
          detail: 'Please enter your transaction PIN',
          life: 3000
        });
        return false;
      }
      // If using saved card, validate card selection
      if (this.paymentMethod() === 'savedCard') {
        if (!this.selectedCardId()) {
          this.messageService.add({
            severity: 'warn',
            summary: 'Validation Error',
            detail: 'Please select a saved card',
            life: 3000
          });
          return false;
        }
      }
    }

    // Cable validation
    if (this.billType.id === 'cable') {
      if (!this.validatedCableCustomer()) {
        this.messageService.add({
          severity: 'warn',
          summary: 'Validation Error',
          detail: 'Please validate smart card number first',
          life: 3000
        });
        return false;
      }
      if (!this.selectedCablePlan()) {
        this.messageService.add({
          severity: 'warn',
          summary: 'Validation Error',
          detail: 'Please select a cable plan',
          life: 3000
        });
        return false;
      }
      // PIN is always required, regardless of payment method
      if (!this.transactionPin() || this.transactionPin().length < 4) {
        this.messageService.add({
          severity: 'warn',
          summary: 'Validation Error',
          detail: 'Please enter your transaction PIN',
          life: 3000
        });
        return false;
      }
      // If using saved card, validate card selection
      if (this.paymentMethod() === 'savedCard') {
        if (!this.selectedCardId()) {
          this.messageService.add({
            severity: 'warn',
            summary: 'Validation Error',
            detail: 'Please select a saved card',
            life: 3000
          });
          return false;
        }
      }
    }

    return true;
  }

  purchaseAirtime(): void {
    const request: ProcessAirtimePaymentRequest = {
      airtimeServiceId: this.selectedServiceId(),
      amount: this.amount()!,
      phoneNumber: this.phoneNumber(),
      transactionPin: this.transactionPin(),
      cardId: this.supportsSavedCards() && this.paymentMethod() === 'savedCard' && this.selectedCardId() ? this.selectedCardId()! : undefined
    };

    this.isProcessing.set(true);
    this.billsPaymentService.purchaseAirtime(request, this.platformId).subscribe({
      next: (response) => {
        this.isProcessing.set(false);
        if (response.success) {
          this.messageService.add({
            severity: 'success',
            summary: 'Payment Successful',
            detail: 'Airtime recharge successful!',
            life: 5000
          });
          // Clear shopping card cache
          const environment = this.settingsService.getEnvironment();
          this.shoppingCardService.clearCache(this.platformId, environment);
          setTimeout(() => {
            this.success.emit();
            this.closeModal();
          }, 2000);
        } else {
          this.messageService.add({
            severity: 'error',
            summary: 'Payment Failed',
            detail: response.message || 'Purchase failed',
            life: 5000
          });
        }
      },
      error: (error) => {
        this.isProcessing.set(false);
        console.error('Error purchasing airtime:', error);
        this.messageService.add({
          severity: 'error',
          summary: 'Error',
          detail: error.error?.message || 'An error occurred while processing your request',
          life: 5000
        });
      }
    });
  }

  purchaseData(): void {
    const dataPlan = this.selectedDataPlan();
    if (!dataPlan) return;

    const request: ProcessDataPaymentRequest = {
      dataServiceId: this.selectedServiceId(),
      phoneNumber: this.phoneNumber(),
      packageSlug: dataPlan.slug,
      billerId: dataPlan.billerId,
      transactionPin: this.transactionPin(),
      cardId: this.supportsSavedCards() && this.paymentMethod() === 'savedCard' && this.selectedCardId() ? this.selectedCardId()! : undefined
    };

    this.isProcessing.set(true);
    this.billsPaymentService.purchaseData(request, this.platformId).subscribe({
      next: (response) => {
        this.isProcessing.set(false);
        if (response.success) {
          this.messageService.add({
            severity: 'success',
            summary: 'Payment Successful',
            detail: 'Data bundle purchased successfully!',
            life: 5000
          });
          const environment = this.settingsService.getEnvironment();
          this.shoppingCardService.clearCache(this.platformId, environment);
          setTimeout(() => {
            this.success.emit();
            this.closeModal();
          }, 2000);
        } else {
          this.messageService.add({
            severity: 'error',
            summary: 'Payment Failed',
            detail: response.message || 'Purchase failed',
            life: 5000
          });
        }
      },
      error: (error) => {
        this.isProcessing.set(false);
        console.error('Error purchasing data:', error);
        this.messageService.add({
          severity: 'error',
          summary: 'Error',
          detail: error.error?.message || 'An error occurred while processing your request',
          life: 5000
        });
      }
    });
  }

  purchaseElectricity(): void {
    const discoPlan = this.selectedDiscoPlan();
    if (!discoPlan) return;

    const request: ProcessElectricityPaymentRequest = {
      electricityServiceId: this.selectedServiceId(),
      amount: this.amount()!,
      meterNumber: this.meterNumber(),
      packageSlug: discoPlan.slug,
      billerSlug: discoPlan.billerSlug,
      billerId: discoPlan.billerId,
      transactionPin: this.transactionPin(),
      cardId: this.supportsSavedCards() && this.paymentMethod() === 'savedCard' && this.selectedCardId() ? this.selectedCardId()! : undefined
    };

    this.isProcessing.set(true);
    this.billsPaymentService.purchaseElectricity(request, this.platformId).subscribe({
      next: (response) => {
        this.isProcessing.set(false);
        if (response.success) {
          this.messageService.add({
            severity: 'success',
            summary: 'Payment Successful',
            detail: 'Electricity payment successful!',
            life: 5000
          });
          const environment = this.settingsService.getEnvironment();
          this.shoppingCardService.clearCache(this.platformId, environment);
          setTimeout(() => {
            this.success.emit();
            this.closeModal();
          }, 2000);
        } else {
          this.messageService.add({
            severity: 'error',
            summary: 'Payment Failed',
            detail: response.message || 'Purchase failed',
            life: 5000
          });
        }
      },
      error: (error) => {
        this.isProcessing.set(false);
        console.error('Error purchasing electricity:', error);
        this.messageService.add({
          severity: 'error',
          summary: 'Error',
          detail: error.error?.message || 'An error occurred while processing your request',
          life: 5000
        });
      }
    });
  }

  purchaseCable(): void {
    const cablePlan = this.selectedCablePlan();
    if (!cablePlan) return;

    const request: ProcessCablePaymentRequest = {
      cableServiceId: this.selectedServiceId(),
      smartCardNumber: this.smartCardNumber(),
      packageSlug: cablePlan.slug,
      billerSlug: cablePlan.billerSlug,
      billerId: cablePlan.billerId,
      transactionPin: this.transactionPin(),
      cardId: this.supportsSavedCards() && this.paymentMethod() === 'savedCard' && this.selectedCardId() ? this.selectedCardId()! : undefined
    };

    this.isProcessing.set(true);
    this.billsPaymentService.purchaseCable(request, this.platformId).subscribe({
      next: (response) => {
        this.isProcessing.set(false);
        if (response.success) {
          this.messageService.add({
            severity: 'success',
            summary: 'Payment Successful',
            detail: 'Cable TV payment successful!',
            life: 5000
          });
          const environment = this.settingsService.getEnvironment();
          this.shoppingCardService.clearCache(this.platformId, environment);
          setTimeout(() => {
            this.success.emit();
            this.closeModal();
          }, 2000);
        } else {
          this.messageService.add({
            severity: 'error',
            summary: 'Payment Failed',
            detail: response.message || 'Purchase failed',
            life: 5000
          });
        }
      },
      error: (error) => {
        this.isProcessing.set(false);
        console.error('Error purchasing cable:', error);
        this.messageService.add({
          severity: 'error',
          summary: 'Error',
          detail: error.error?.message || 'An error occurred while processing your request',
          life: 5000
        });
      }
    });
  }

  closeModal(): void {
    this.visibleChange.emit(false);
    this.resetForm();
  }

  resetForm(): void {
    this.selectedServiceId.set('');
    this.amount.set(null);
    this.phoneNumber.set('');
    this.transactionPin.set('');
    this.selectedPeriod.set('');
    this.dataPlans.set([]);
    this.selectedDataPlan.set(null);
    this.meterNumber.set('');
    this.discoPlans.set([]);
    this.selectedDiscoPlan.set(null);
    this.validatedCustomer.set(null);
    this.smartCardNumber.set('');
    this.cablePlans.set([]);
    this.selectedCablePlan.set(null);
    this.validatedCableCustomer.set(null);
    this.paymentMethod.set('shoppingCard');
    this.selectedCardId.set(null);
  }

  getMaskedCardNumber(last4?: string): string {
    if (!last4) return '**** **** **** ****';
    return `**** **** **** ${last4}`;
  }
}
